/**
 * React hook for managing user identity
 */
import { useState, useEffect, useCallback } from 'react';
import { Identity, base58 } from '@amorphous/core';
import { saveIdentity, loadIdentity } from '../storage/indexeddb';

interface UseIdentityResult {
  identity: Identity | null;
  loading: boolean;
  error: Error | null;
  createIdentity: () => Promise<void>;
  clearIdentity: () => Promise<void>;
  importIdentity: (privateKey: Uint8Array, publicKey: Uint8Array) => Promise<void>;
}

// Quick localStorage access (IndexedDB can be slow on mobile)
function getQuickIdentity(): Identity | null {
  try {
    const json = localStorage.getItem('aos_identity');
    if (json) {
      return Identity.fromJSON(json);
    }
  } catch {
    // Ignore errors
  }
  return null;
}

function saveQuickIdentity(identity: Identity): void {
  try {
    localStorage.setItem('aos_identity', identity.toJSON());
  } catch {
    // Ignore errors
  }
}

export function useIdentity(): UseIdentityResult {
  // Try localStorage first for instant load
  const [identity, setIdentity] = useState<Identity | null>(getQuickIdentity);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Load identity on mount (also try IndexedDB as backup)
  useEffect(() => {
    async function load() {
      try {
        // If we already have identity from localStorage, skip IndexedDB load
        if (identity) {
          setLoading(false);
          return;
        }
        const json = await loadIdentity();
        if (json) {
          const loaded = Identity.fromJSON(json);
          setIdentity(loaded);
          saveQuickIdentity(loaded); // Sync to localStorage
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load identity'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [identity]);

  const createIdentity = useCallback(async () => {
    try {
      console.log('[Identity] Generating new identity...');
      const newIdentity = Identity.generate();
      console.log('[Identity] Generated:', newIdentity.shortId);

      // Save to localStorage first (fast)
      saveQuickIdentity(newIdentity);
      setIdentity(newIdentity);

      // Then save to IndexedDB (may be slow/fail on mobile)
      saveIdentity(newIdentity.toJSON()).catch(err => {
        console.warn('[Identity] IndexedDB save failed:', err);
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create identity'));
      throw err;
    }
  }, []);

  const clearIdentity = useCallback(async () => {
    try {
      await saveIdentity('');
      localStorage.removeItem('aos_identity');
      setIdentity(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to clear identity'));
      throw err;
    }
  }, []);

  const importIdentity = useCallback(async (privateKey: Uint8Array, publicKey: Uint8Array) => {
    try {
      console.log('[Identity] Importing identity from backup...');

      // Create JSON in the format expected by Identity.fromJSON
      const json = JSON.stringify({
        privateKey: base58.encode(privateKey),
        publicKey: base58.encode(publicKey),
      });

      const imported = Identity.fromJSON(json);
      console.log('[Identity] Imported:', imported.shortId);

      // Save to localStorage first (fast)
      saveQuickIdentity(imported);
      setIdentity(imported);

      // Then save to IndexedDB
      saveIdentity(imported.toJSON()).catch(err => {
        console.warn('[Identity] IndexedDB save failed:', err);
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to import identity'));
      throw err;
    }
  }, []);

  return { identity, loading, error, createIdentity, clearIdentity, importIdentity };
}
